import { Table } from "common/Table/Table";
import { Fragment, useEffect, useState } from "react";
import UserService from "services/user.service";
import { InfoSessions } from "components/contacts/InfoSessions";
import { Scholarships } from "components/contacts/Scholarships";
import { useDebounce } from "hooks/useDebounce";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Tooltip } from "common/Tooltip";
import { exportToCSV, formatDate, formatDateTime } from "utils/funcs";
import type { ExportColumnDef } from "utils/funcs";
import { useNotifications } from "hooks/useNotification";
import swal from "sweetalert";
import classNames from "classnames";
import {
  CONTACT,
  LEADER,
  MANAGEMENT,
  NAMES,
  SESSIONS,
  UPGRADE,
} from "utils/constants";
import { Modal } from "common/Modal";
import { useRouter } from "hooks/useRouter";
import moment from "moment";
import { DATE } from "./trainings/WeeklyStats";
import { MetricCard } from "./BlackSparrow";
import { Loader } from "common/Loader";

const service = new UserService();

export const formatPhone = (phone) => {
  let phoneWithCode = "";
  if (phone) {
    if (String(phone)?.includes("+1")) {
      if (String(phone).length > 4) {
        const firstDigits = String(phone).slice(2, 5);
        phoneWithCode = `(${firstDigits}) ${String(phone).slice(3, 6)}-${String(
          phone
        ).slice(6, 10)}`;
      } else {
        phoneWithCode = phone;
      }
    } else {
      if (!String(phone)?.includes("(") && String(phone).length > 4) {
        const firstDigits = String(phone).slice(0, 3);
        phoneWithCode = `(${firstDigits}) ${String(phone).slice(3, 6)}-${String(
          phone
        ).slice(6, 10)}`;
      } else {
        phoneWithCode = phone;
      }
    }
  }

  return phoneWithCode;

  // if (phone && String(phone).length > 7) {
  //   return `(${String(phone)?.slice(0, 3)}) ${String(phone)?.slice(
  //     3,
  //     6
  //   )}-${String(phone)?.slice(6, 10)}`;
  // } else {
  //   return phone;
  // }
};

const Stage = ({ stage, firstName, lastName }) => {
  const getColor = (s) => {
    if (s === "Contact") {
      return "border-[#EE8332]";
    } else if (s === "Contact+") {
      return "border-[#EE8332]";
    } else if (s === "SH Pro") {
      return "border-[#7715F5]";
    } else if (s === "SH Pro+") {
      return "border-[#7715F5]";
    } else if (s === "Recruiter") {
      return "border-[#57C1F9]";
    } else if (s === "Recruiter+") {
      return "border-[#57C1F9]";
    } else if (s === "Signed") {
      return "border-[#02F900]";
    } else if (s === "Signed+") {
      return "border-[#02F900]";
    } else {
      return "border-[#EE8332]";
    }
  };

  return (
    <div
      className={`border-2 ${getColor(
        stage
      )} rounded-full w-12 h-12 text-sm flex items-center justify-center flex-shrink-0`}
    >
      {firstName?.charAt(0) + lastName?.charAt(0)}
    </div>
  );
};

export const Contacts = () => {
  const [showColumns, setShowColumns] = useState({
    names: false,
    contacts: false,
    upgrade: false,
    sessions: false,
    management: false,
    leader: false,
  });

  const columns = [
    {
      key: "badge_id",
      dbColName: "badge_id",
      title: "badge id",
      freeze: true,
      isSort: true,
      color: "bg-[#BDBDBD] text-white",
      freezeIndex: "left-0",
    },
    {
      key: "contact_full_name",
      title: "full name",
      color: "bg-[#BDBDBD] text-white",
      render: (d) =>
        !d?.full_name?.includes("undefined") && d?.full_name
          ? d.full_name
          : `${d?.first_name ? d?.first_name : ""} ${d?.last_name ? d?.last_name : ""
          }`,
      header: () => (
        <button
          type="button"
          onClick={() =>
            setShowColumns((prev) => ({ ...prev, names: !prev.names }))
          }
        >
          {showColumns.names ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
              />
            </svg>
          )}
        </button>
      ),
    },
    {
      key: "first_name",
      dbColName: "first_name",
      title: "first name",
      isSort: true,
      show: false,
      freeze: true,
      color: "bg-[#BDBDBD] text-white",
      freezeIndex: "left-20",
    },
    {
      key: "last_name",
      dbColName: "last_name",
      title: "last name",
      freeze: true,
      isSort: true,
      show: false,
      color: "bg-[#BDBDBD] text-white",
      freezeIndex: "left-44",
    },
    {
      key: "phone",
      dbColName: "phone",
      isSort: true,
      show: false,
      title: "phone",
      color: "bg-[#BDBDBD] text-white",
      render: (phone: string) => formatPhone(phone),
    },
    {
      key: "email",
      dbColName: "email",
      title: "email",
      show: false,
      isSort: true,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "stage",
      title: "current stage",
      isSort: true,
      color: "bg-[#BDBDBD] text-white",
      // No dbColName: Table passes the full row into render (needed for initials).
      // Export injects `stage` explicitly in handleExport as "current stage".
      render: (d) => (
        <div className="flex items-center gap-2">
          <Stage
            stage={d.stage}
            firstName={d.first_name}
            lastName={d.last_name}
          />
          <span className="text-sm whitespace-nowrap">{d.stage || ""}</span>
        </div>
      ),
    },
    {
      key: "source",
      dbColName: "source",
      isSort: true,
      title: "source",
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "contact_leader_id",
      dbColName: "contact_leader_id",
      title: "contact leader id",
      isSort: true,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "contact_leader_name",
      dbColName: "contact_leader_name",
      isSort: true,
      title: "contact leader name",
      color: "bg-[#BDBDBD] text-white",
    },
    // {
    //   key: "location",
    //   dbColName: "location",
    //   isSort: true,
    //   title: "location",
    //   color: "bg-[#BDBDBD] text-white",
    // },
    {
      key: "location",
      dbColName: "location",
      isSort: true,
      title: "location",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "full_name",
      dbColName: "full_name",
      title: "full name",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "personal_email",
      dbColName: "personal_email",
      title: "personal email",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "cell_phone",
      dbColName: "cell_phone",
      title: "cell phone",
      show: false,
      color: "bg-[#BDBDBD] text-white",
      render: (phone: string) => formatPhone(phone),
    },
    {
      key: "uid",
      dbColName: "uid",
      title: "uid",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "d2d_exp",
      dbColName: "d2d_exp",
      title: "d2d exp",
      isSort: true,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "other",
      dbColName: "other",
      title: "other",
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "notes",
      dbColName: "notes",
      isSort: true,
      title: "notes",
      color: "bg-[#BDBDBD] text-white",
      render: (notes) => (
        <Tooltip message={notes}>
          {notes && notes?.toString()?.slice(0, 40)}
        </Tooltip>
      ),
    },
    {
      key: "date_submitted",
      isSort: true,
      dbColName: "date_submitted",
      title: "date submitted",
      color: "bg-[#BDBDBD] text-white",
      render: (date) => formatDate(date),
    },
    // {
    //   key: "cell_phone",
    //   dbColName: "cell_phone",
    //   title: "cell phone",
    //   isSort: true,
    //   color: "bg-[#BDBDBD] text-white",
    //   render: (phone: string) => formatPhone(phone),
    // },
    {
      key: "max_position",
      dbColName: "max_position",
      isSort: true,
      title: "max position",
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "tenure",
      dbColName: "tenure",
      isSort: true,
      title: "tenure",
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "contact_created",
      dbColName: "contact_created",
      title: "contact created",
      color: "bg-[#BDBDBD] text-white",
      render: (date) => formatDate(date),
      header: () => (
        <button
          type="button"
          onClick={() =>
            setShowColumns((prev) => ({ ...prev, contacts: !prev.contacts }))
          }
        >
          {showColumns.contacts ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
              />
            </svg>
          )}
        </button>
      ),
    },
    {
      key: "first_contact_created",
      dbColName: "first_contact_created",
      isSort: true,
      title: "first contact created date",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "first_contact_plus_created",
      dbColName: "first_contact_plus_created",
      isSort: true,
      title: "first contact+ date",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "first_signed_created",
      dbColName: "first_signed_created",
      title: "first signed date",
      isSort: true,
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "first_signed_plus_created",
      dbColName: "first_signed_plus_created",
      title: "first signed+ date",
      isSort: true,
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "first_recruiter_created",
      dbColName: "first_recruiter_created",
      isSort: true,
      title: "first recruiter date",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "first_recruiter_plus_created",
      dbColName: "first_recruiter_plus_created",
      isSort: true,
      title: "first recruiter+",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "first_shpro_created",
      dbColName: "first_shpro_created",
      title: "first sh pro date",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "first_shpro_plus_created",
      dbColName: "first_shpro_plus_created",
      isSort: true,
      title: "first sh pro+ date",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date) || (date ? String(date) : ""),
    },
    {
      key: "max_stage_date",
      dbColName: "max_stage_date",
      isSort: true,
      title: "max stage date",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date),
    },
    {
      key: "last_achievement_date",
      dbColName: "last_achievement_date",
      isSort: true,
      title: "last achievement date",
      color: "bg-[#4DD0E0] text-white",
      render: (date) => formatDateTime(date),
    },
    {
      key: "last_achievement",
      dbColName: "last_achievement",
      title: "previous stage (last achievement)",
      isSort: true,
      color: "bg-[#4DD0E0] text-white",
    },
    {
      key: "days_since_last_achievement",
      dbColName: "days_since_last_achievement",
      isSort: true,
      title: "days since last achievement",
      color: "bg-[#4DD0E0] text-white",
      header: () => (
        <button
          type="button"
          onClick={() =>
            setShowColumns((prev) => ({ ...prev, sessions: !prev.sessions }))
          }
        >
          {showColumns.sessions ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
              />
            </svg>
          )}
        </button>
      ),
    },

    {
      key: "online_info_session_registered",
      dbColName: "online_info_session_registered",
      isSort: true,
      color: "bg-[#78909C] text-white",
      show: false,
      title: "online info session (registered)",
    },
    {
      key: "online_info_session_attended",
      dbColName: "online_info_session_attended",
      show: false,
      color: "bg-[#78909C] text-white",
      title: "online info session (attended)",
      isSort: true,
    },
    {
      key: "weekly_training_rsvp",
      dbColName: "weekly_training_rsvp",
      show: false,
      color: "bg-[#78909C] text-white",
      title: "weekly training (rsvp)",
      render: (date) => formatDate(date),
      isSort: true,
    },
    {
      key: "weekly_training_attended",
      dbColName: "weekly_training_attended",
      show: false,
      color: "bg-[#78909C] text-white",
      render: (date) => formatDate(date),
      title: "weekly training (attended)",
    },
    {
      key: "application_submitted",
      dbColName: "application_submitted",
      show: false,
      isSort: true,
      color: "bg-[#78909C] text-white",
      title: "application submitted",
    },

    {
      key: "agreement_sent",
      dbColName: "agreement_sent",
      title: "agreement sent",
      show: false,
      isSort: true,
      color: "bg-[#78909C] text-white",
      render: (date) => formatDate(date),
    },
    {
      key: "agreement_signed",
      dbColName: "agreement_signed",
      show: false,
      title: "agreement signed",
      isSort: true,
      color: "bg-[#78909C] text-white",
      render: (date) => formatDate(date),
    },
    {
      key: "onboard_date",
      dbColName: "onboard_date",
      title: "onboard date",
      show: false,
      isSort: true,
      color: "bg-[#78909C] text-white",
      render: (date) => formatDate(date),
    },
    {
      key: "last_street_genie_login",
      dbColName: "last_street_genie_login",
      title: "last street genie login",
      color: "bg-[#78909C] text-white",
      show: false,
      isSort: true,
      render: (date) => formatDate(date),
    },
    {
      key: "completed_onboard_training",
      dbColName: "completed_onboard_training",
      isSort: true,
      show: false,
      title: "completed onboard training",
      color: "bg-[#78909C] text-white",
      render: (date) => formatDate(date),
    },
    {
      key: "manager_interview",
      dbColName: "manager_interview",
      isSort: true,
      show: false,
      title: "manager interview",
      color: "bg-[#78909C] text-white",
      render: (date) => formatDate(date),
    },

    {
      key: "preseason_blitz_registered",
      isSort: true,
      dbColName: "preseason_blitz_registered",
      show: false,
      title: "preseason blitz registered",
      color: "bg-[#78909C] text-white",
    },
    {
      key: "preseason_blitz_attended",
      dbColName: "preseason_blitz_attended",
      isSort: true,
      show: false,
      title: "preseason blitz attended",
      color: "bg-[#78909C] text-white",
    },
    {
      key: "list_started_100",
      isSort: true,
      dbColName: "list_started_100",
      color: "bg-[#78909C] text-white",
      show: false,
      title: "100 list started",
    },
    {
      key: "list_completed_100",
      dbColName: "list_completed_100",
      color: "bg-[#78909C] text-white",

      title: "100 list completed",
      header: () => (
        <button
          type="button"
          onClick={() =>
            setShowColumns((prev) => ({ ...prev, upgrade: !prev.upgrade }))
          }
        >
          {showColumns.upgrade ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
              />
            </svg>
          )}
        </button>
      ),
    },

    {
      key: "first_upgrade_date",
      isSort: true,
      dbColName: "first_upgrade_date",
      title: "first upgrade date",
      show: false,
      color: "bg-[#24a69a] text-white",
    },
    {
      key: "first_install_date",
      dbColName: "first_install_date",
      color: "bg-[#24A69A] text-white",
      show: false,
      title: "first install date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_install_date",
      dbColName: "last_install_date",
      color: "bg-[#24A69A] text-white",
      isSort: true,
      title: "last install date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_credit_run",
      dbColName: "last_credit_run",
      color: "bg-[#24A69A] text-white",
      isSort: true,
      title: "last credit run",
      render: (date) => formatDate(date),
    },
    {
      key: "last_sh_date",
      dbColName: "last_sh_install",
      color: "bg-[#24A69A] text-white",
      show: false,
      title: "last sh install date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_solar_lead_date",
      dbColName: "last_solar_lead_date",
      color: "bg-[#24A69A] text-white",
      title: "last solar lead date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_solar_closed_date",
      dbColName: "last_solar_closed_date",
      color: "bg-[#24A69A] text-white",
      show: false,
      title: "last solar closed date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_solar_permit_date",
      dbColName: "last_solar_permit",
      color: "bg-[#24A69A] text-white",
      show: false,
      title: "last solar permit date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_solar_install_date",
      dbColName: "last_solar_install_date",
      color: "bg-[#24A69A] text-white",
      show: false,
      title: "last solar install date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_recruit_with_sale_date",
      dbColName: "last_recruit_with_sale_date",
      color: "bg-[#24A69A] text-white",
      show: false,
      isSort: true,
      title: "last recruit with sale date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_recruit_sh_date",
      dbColName: "last_recruit_sh_date",
      color: "bg-[#24A69A] text-white",
      show: false,
      isSort: true,
      title: "last recruit sh date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_signed_recruit_date",
      dbColName: "last_signed_recruit_date",
      color: "bg-[#24A69A] text-white",
      show: false,
      isSort: true,
      title: "last signed recruit date",
      render: (date) => formatDate(date),
    },
    {
      key: "last_signed_contact_date",
      dbColName: "last_signed_contact_date",
      color: "bg-[#24A69A] text-white",
      show: false,
      isSort: true,
      title: "last signed contact date",
      render: (date) => formatDate(date),
    },
    // {
    //   key: "rafp",
    //   title: "Rafp+",
    //   dbColName: "rafp",
    //   color: "bg-[#5B95F9] text-white",
    // },
    {
      key: "rafp",
      dbColName: "rafp",
      show: false,
      color: "bg-[#5B95F9] text-white",
      title: "rafp",
    },
    {
      key: "fp",
      dbColName: "fp",
      color: "bg-[#5B95F9] text-white",
      title: "fp",
    },
    {
      key: "fp_plus",
      dbColName: "fp_plus",
      color: "bg-[#5B95F9] text-white",
      title: "fp +",
    },
    {
      key: "opps",
      dbColName: "oops",
      color: "bg-[#5B95F9] text-white",
      title: "opps",
    },
    {
      key: "cs",
      dbColName: "cs",
      color: "bg-[#5B95F9] text-white",
      title: "cs",
    },
    {
      key: "in",
      dbColName: "in",
      color: "bg-[#5B95F9] text-white",
      title: "ins",
    },
    {
      key: "contacts",
      dbColName: "contacts",
      color: "bg-[#5B95F9] text-white",
      title: "contacts",
    },
    {
      key: "signed_reps",
      dbColName: "signed_contacts",
      color: "bg-[#5B95F9] text-white",
      title: "signed contacts",
    },
    {
      key: "rws",
      dbColName: "rws",
      color: "bg-[#5B95F9] text-white",
      title: "rws",
    },
    {
      key: "cwra",
      dbColName: "cwra",
      color: "bg-[#5B95F9] text-white",
      title: "cwra",
    },
    {
      key: "cfp",
      dbColName: "cfp",
      color: "bg-[#5B95F9] text-white",
      title: "cfp",
    },
    {
      key: "ccs",
      dbColName: "ccs",
      color: "bg-[#5B95F9] text-white",
      title: "ccs",
    },
    {
      key: "cin",
      dbColName: "cin",
      color: "bg-[#5B95F9] text-white",
      title: "cin",
      header: () => (
        <button
          type="button"
          onClick={() =>
            setShowColumns((prev) => ({
              ...prev,
              management: !prev.management,
            }))
          }
        >
          {showColumns.management ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
              />
            </svg>
          )}
        </button>
      ),
    },

    {
      key: "team",
      dbColName: "team",
      title: "team",
      show: false,
      color: "bg-[#F8CB4D] text-white",
    },
    {
      key: "management_group",
      dbColName: "management_group",
      show: false,
      title: "management group",
      color: "bg-[#F8CB4D] text-white",
    },
    {
      key: "senior_management_group",
      dbColName: "senior_management_group",
      title: "senior management group",
      show: false,
      color: "bg-[#F8CB4D] text-white",
    },
    {
      key: "region",
      dbColName: "region",
      title: "region",
      show: false,
      color: "bg-[#F8CB4D] text-white",
    },
    {
      key: "senior_region",
      dbColName: "senior_region",
      color: "bg-[#F8CB4D] text-white",
      show: false,
      title: "senior region",
    },
    {
      key: "partnership",
      dbColName: "partnership",
      show: false,
      title: "partnership",
      color: "bg-[#F8CB4D] text-white",
    },
    {
      key: "division",
      dbColName: "division",
      title: "division",
      show: false,
      color: "bg-[#F8CB4D] text-white",
      header: () => (
        <button
          type="button"
          onClick={() =>
            setShowColumns((prev) => ({ ...prev, leader: !prev.leader }))
          }
        >
          {showColumns.leader ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
              />
            </svg>
          )}
        </button>
      ),
    },

    {
      key: "contact_leader_phone_number",
      dbColName: "contact_leader_phone",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "contact leader phone number",
    },
    {
      key: "contact_leader_email",
      show: false,
      dbColName: "contact_leader_email",
      color: "bg-[#F36524] text-white",
      title: "contact leader email",
    },
    {
      key: "recruiter_id",
      dbColName: "recruiter_id",
      show: false,
      title: "recruiter id",
      color: "bg-[#F36524] text-white",
    },
    {
      key: "recruiter_name",
      dbColName: "recruiter_name",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "recruiter name",
    },
    {
      key: "recruiter_phone_number",
      dbColName: "recruiter_phone_number",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "recruiter phone number",
    },
    {
      key: "recruiter_email",
      show: false,
      dbColName: "recruiter_email",
      color: "bg-[#F36524] text-white",
      title: "recruiter email",
    },
    {
      key: "team_leader_id",
      dbColName: "team_leader_id",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "team leader id",
    },
    {
      key: "team_leader_name",
      dbColName: "team_leader_name",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "team leader name",
    },
    {
      key: "team_leader_phone_number",
      dbColName: "team_leader_phone_number",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "team leader phone number",
      render: (phone) => formatPhone(phone),
    },
    {
      key: "team_leader_email",
      dbColName: "team_leader_email",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "team leader email",
    },
    {
      key: "management_group_leader_id",
      dbColName: "management_group_leader_id",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "management group leader id",
    },
    {
      key: "management_group_leader_name",
      dbColName: "management_group_leader_name",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "management group leader name",
    },
    {
      key: "management_group_leader_phone_number",
      dbColName: "management_group_leader_phone_number",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "management group leader phone number",
      render: (phone) => formatPhone(phone),
    },
    {
      key: "management_group_leader_email",
      dbColName: "management_group_leader_email",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "management group leader email",
    },
    {
      key: "senior_management_group_leader_id",
      dbColName: "senior_management_group_leader_id",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "senior management group leader id",
    },
    {
      key: "senior_management_group_leader",
      dbColName: "senior_management_group_leader",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "senior management group leader",
    },
    {
      key: "senior_management_group_leader_phone_number",
      dbColName: "senior_management_group_leader_phone_number",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "senior management group leader phone number",
      render: (phone) => formatPhone(phone),
    },
    {
      key: "senior_management_group_leader_email",
      dbColName: "senior_management_group_leader_email",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "senior management group leader email",
    },
    {
      key: "region_leader_id",
      dbColName: "region_leader_id",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "region leader id",
    },
    {
      key: "region_leader_name",
      dbColName: "region_leader_name",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "region leader name",
    },
    {
      key: "region_leader_phone_number",
      show: false,
      dbColName: "region_leader_phone_number",
      color: "bg-[#F36524] text-white",
      title: "region leader phone number",
      render: (phone) => formatPhone(phone),
    },
    {
      key: "region_leader_email",
      dbColName: "region_leader_email",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "region leader email",
    },
    {
      key: "senior_region_id",
      dbColName: "senior_region_id",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "senior region id",
    },
    {
      key: "senior_region_name",
      dbColName: "senior_region_name",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "senior region name",
    },
    {
      key: "senior_region_leader_phone_number",
      dbColName: "senior_region_leader_phone_number",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "senior region leader phone number",
      render: (phone) => formatPhone(phone),
    },
    {
      key: "senior_region_leader_email",
      dbColName: "senior_region_leader_email",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "senior region leader email",
    },
    {
      key: "partner_leader_id",
      dbColName: "partner_leader_id",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "partner leader id",
    },
    {
      key: "partner_leader_name",
      dbColName: "partner_leader_name",
      show: false,
      color: "bg-[#F36524] text-white",
      title: "partner leader name",
    },
    {
      key: "partner_leader_phone_number",
      dbColName: "partner_leader_phone_number",
      color: "bg-[#F36524] text-white",
      title: "partner leader phone number",
      render: (phone) => formatPhone(phone),
      show: false,
    },
    {
      key: "division_leader_id",
      dbColName: "division_leader_id",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "division leader id",
    },
    {
      key: "division_leader_name",
      dbColName: "division_leader_name",
      color: "bg-[#F36524] text-white",
      show: false,
      title: "division leader name",
    },
    {
      key: "division_leader_phone_number",
      dbColName: "division_leader_phone_number",
      color: "bg-[#F36524] text-white",
      show: false,
      render: (phone) => formatPhone(phone),
      title: "division leader phone number",
    },

    {
      key: "reminder_text_date",
      dbColName: "reminder_text_date",
      title: "reminder text date",
      show: false,
      color: "bg-[#BDBDBD] text-white",
      render: (date) => formatDateTime(date),
    },
    {
      key: "reminder_text_time",
      dbColName: "reminder_text_time",
      title: "reminder text time",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "reminder_text_timezone",
      dbColName: "reminder_text_timezone",
      title: "reminder text timezone",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "social_website",
      dbColName: "social_website",
      title: "social website",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "datesubmitted",
      dbColName: "datesubmitted",
      title: "datesubmitted",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "updatedAt",
      dbColName: "updatedAt",
      title: "updated at",
      show: false,
      color: "bg-[#BDBDBD] text-white",
      render: (date) => formatDateTime(date),
    },

    // Legacy / marketing fields (often empty; kept for A–DU sheet parity)
    {
      key: "ad_source",
      dbColName: "ad_source",
      title: "ad source",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "ad_category",
      dbColName: "ad_category",
      title: "ad category",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "duplicate_id",
      dbColName: "duplicate_id",
      title: "duplicate id",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "duplicate_phone",
      dbColName: "duplicate_phone",
      title: "duplicate phone",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "duplicate_email",
      dbColName: "duplicate_email",
      title: "duplicate email",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "campusreel_direct_applicants",
      dbColName: "campusreel_direct_applicants",
      title: "campusreel direct applicants",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "campusreel_coregistration",
      dbColName: "campusreel_coregistration",
      title: "campusreel coregistration",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "campusreel_scholarship_email",
      dbColName: "campusreel_scholarship_email",
      title: "campusreel scholarship email",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "campusreel_scholarship_ytd",
      dbColName: "campusreel_scholarship_ytd",
      title: "campusreel scholarship ytd",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "campusreel_form_completed",
      dbColName: "campusreel_form_completed",
      title: "campusreel form completed",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "digitalhire_candidates",
      dbColName: "digitalhire_candidates",
      title: "digitalhire candidates",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "digitalhire_summer_outreach",
      dbColName: "digitalhire_summer_outreach",
      title: "digitalhire summer outreach",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "evo_website",
      dbColName: "evo_website",
      title: "evo website",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "evo_scholarship_bonus",
      dbColName: "evo_scholarship_bonus",
      title: "evo scholarship bonus",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },
    {
      key: "digital_recruit",
      dbColName: "digital_recruit",
      title: "digital recruit",
      show: false,
      color: "bg-[#BDBDBD] text-white",
    },

    {
      key: "createdAt",
      dbColName: "createdAt",
      title: "created at",
      render: (date) => formatDateTime(date),
    },
    {
      key: "action",
      dbColName: "_id",
      freeze: true,
      freezeIndex: "right-0",
      title: "action",
      render: (id) => {
        return (
          <button
            type="button"
            className="text-right text-red-500"
            onClick={() => handleDelete(id)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
              />
            </svg>
          </button>
        );
      },
    },
  ];

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState("YEARTODATE");
  const [totalItems, setTotalItems] = useState(0);
  const [exporting, setExporting] = useState(false);
  const { push } = useRouter();

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100,
    source: "",
    location: "",
    maxPosition: "",
    contactCreatedStartDate: "",
    contactCreatedEndDate: "",
    division: "Evolution",
    fullName: "",
    recruiterName: "",
    recruiterbadgeId: "",
    groupBy: "",
    tenure: "",
    stage: "",
    email: "",
    isDownload: "false",
    team: "",
  });
  const [tab, setTab] = useState("Contacts");
  const [loadingStats, setLoadingStats] = useState(false);
  const [stats, setStats] = useState([]);
  /** Matches ranks/recruiting total: distinct phones in scope (sum of stage cards can be higher). */
  const [statsUniqueTotal, setStatsUniqueTotal] = useState<number | null>(null);

  const debounced = useDebounce(pagination, 500);

  const { successMessage } = useNotifications();

  useEffect(() => {
    setLoading(true);
    service
      .getContacts({ ...debounced, date })
      .then((resp) => {
        if (resp.data.success) {
          setTotalItems(resp.data.totalDocument);
          setData(resp.data.data);
        }
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setLoading(false);
      });
  }, [debounced, date]);

  useEffect(() => {
    setLoadingStats(true);
    service
      .getContactStats({ date, division: pagination.division })
      .then((resp) => {
        if (resp.data.success) {
          setStats(resp.data.totalDocument);
          const u = resp.data.uniquePhoneTotal;
          setStatsUniqueTotal(u != null ? Number(u) : null);
        }
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setLoadingStats(false);
      });
  }, [date, pagination.division]);

  const handleExport = () => {
    setExporting(true);
    const pagin: any = { ...pagination, isDownload: "true" };
    delete pagin.pageNumber;
    delete pagin.pageSize;

    /** Contact date / datetime fields → readable MM/DD/YYYY h:mm:ss A in the spreadsheet. */
    const isDateExportField = (name: string) => {
      const n = String(name || "").toLowerCase();
      if (!n) return false;
      if (
        n === "createdat" ||
        n === "updatedat" ||
        n === "contact_created" ||
        n === "date_submitted" ||
        n === "datesubmitted" ||
        n === "agreement_sent" ||
        n === "agreement_signed" ||
        n === "onboard_date" ||
        n === "last_street_genie_login" ||
        n === "last_credit_run" ||
        n === "completed_onboard_training" ||
        n === "application_submitted" ||
        n === "manager_interview" ||
        n === "online_info_session_registered" ||
        n === "online_info_session_attended" ||
        n === "weekly_training_rsvp" ||
        n === "weekly_training_attended" ||
        n === "list_started_100" ||
        n === "list_completed_100" ||
        n === "100_list_started" ||
        n === "100_list_completed" ||
        n === "preseason_blitz_registered" ||
        n === "preseason_blitz_attended" ||
        n === "max_stage_date" ||
        n === "last_achievement_date" ||
        n === "first_contact_created" ||
        n === "first_contact_plus_created" ||
        n === "first_signed_created" ||
        n === "first_signed_plus_created" ||
        n === "first_recruiter_created" ||
        n === "first_recruiter_plus_created" ||
        n === "first_shpro_created" ||
        n === "first_shpro_plus_created"
      ) {
        return true;
      }
      return (
        n.endsWith("_date") ||
        n.endsWith("_at") ||
        n.includes("_date") ||
        n.endsWith("_created") ||
        n.endsWith("_signed") ||
        n.endsWith("_login") ||
        n.endsWith("_attended") ||
        n.endsWith("_registered") ||
        n.endsWith("_rsvp") ||
        n.endsWith("_interview") ||
        n.endsWith("_training")
      );
    };

    const omitExportDbCols = new Set([
      "deleted",
      "enriched_at",
      "enrichment_version",
      "is_deleted",
      "last_recruit_sh_date",
      "last_sh_install",
      "oops",
      "cs",
      "in",
      "ccs",
      "cin",
      // Client sheet: omit PII / internal / solar columns (keep 100-list dates in export).
      "location",
      "personal_email",
      "date_submitted",
      "datesubmitted",
      "application_submitted",
      "last_solar_lead_date",
      "last_solar_closed_date",
      "last_solar_permit",
      "last_solar_permit_date",
      "last_solar_install_date",
      "updatedAt",
      "updated_at",
    ]);
    const omitExportKeys = new Set([
      "action",
      "contact_full_name",
      "opps",
      "last_sh_date",
      "location",
      "personal_email",
      "date_submitted",
      "datesubmitted",
      "application_submitted",
      "last_solar_lead_date",
      "last_solar_closed_date",
      "last_solar_permit",
      "last_solar_install_date",
    ]);

    const exportColumns: ExportColumnDef[] = columns
      .filter(
        (c) =>
          Boolean(c.dbColName) &&
          !omitExportKeys.has(String(c.key)) &&
          !omitExportDbCols.has(String(c.dbColName))
      )
      .map((c) => {
        const dbColName = String(c.dbColName);
        // Prefer the Contacts table heading; never export raw Mongo field names.
        const rawTitle = String(c.title ?? "").trim();
        const title =
          rawTitle && rawTitle !== dbColName
            ? rawTitle
            : rawTitle
              ? rawTitle.replace(/_/g, " ")
              : dbColName.replace(/_/g, " ");
        const col: ExportColumnDef = {
          dbColName,
          title,
        };
        if (isDateExportField(dbColName)) {
          col.format = "datetime";
        }
        return col;
      });

    // Stage has no dbColName (UI badge needs full row) — always include it in export.
    if (!exportColumns.some((c) => c.dbColName === "stage")) {
      const insertAt = exportColumns.findIndex((c) => c.dbColName === "source");
      exportColumns.splice(insertAt >= 0 ? insertAt : 5, 0, {
        dbColName: "stage",
        title: "current stage",
      });
    } else {
      const stageCol = exportColumns.find((c) => c.dbColName === "stage");
      if (stageCol) stageCol.title = "current stage";
    }

    // Guarantee core enrichment columns (even if UI column defs drift).
    for (const col of [
      { dbColName: "stage", title: "current stage" },
      { dbColName: "max_position", title: "max position" },
      {
        dbColName: "first_contact_created",
        title: "first contact created date",
        format: "datetime" as const,
      },
      {
        dbColName: "first_contact_plus_created",
        title: "first contact+ date",
        format: "datetime" as const,
      },
      {
        dbColName: "first_signed_created",
        title: "first signed date",
        format: "datetime" as const,
      },
      {
        dbColName: "first_signed_plus_created",
        title: "first signed+ date",
        format: "datetime" as const,
      },
      {
        dbColName: "first_recruiter_created",
        title: "first recruiter date",
        format: "datetime" as const,
      },
      {
        dbColName: "first_recruiter_plus_created",
        title: "first recruiter+",
        format: "datetime" as const,
      },
      {
        dbColName: "first_shpro_created",
        title: "first sh pro date",
        format: "datetime" as const,
      },
      {
        dbColName: "first_shpro_plus_created",
        title: "first sh pro+ date",
        format: "datetime" as const,
      },
      {
        dbColName: "max_stage_date",
        title: "max stage date",
        format: "datetime" as const,
      },
      {
        dbColName: "last_achievement",
        title: "previous stage (last achievement)",
      },
      { dbColName: "last_street_genie_login", title: "last street genie login" },
      { dbColName: "last_credit_run", title: "last credit run" },
      { dbColName: "last_install_date", title: "last install date" },
      {
        dbColName: "list_started_100",
        title: "100 list started",
        format: "datetime" as const,
      },
      {
        dbColName: "list_completed_100",
        title: "100 list completed",
        format: "datetime" as const,
      },
      {
        dbColName: "application_submitted",
        title: "application submitted",
        format: "datetime" as const,
      },
      {
        dbColName: "date_submitted",
        title: "date submitted",
        format: "datetime" as const,
      },
    ]) {
      if (!exportColumns.some((c) => c.dbColName === col.dbColName)) {
        exportColumns.push({
          ...col,
          format:
            col.format ??
            (isDateExportField(col.dbColName) ? "datetime" : undefined),
        });
      } else if (col.dbColName === "last_achievement" || col.dbColName === "stage") {
        const existing = exportColumns.find((c) => c.dbColName === col.dbColName);
        if (existing && col.title) existing.title = col.title;
      }
    }
    service
      .getContacts({ ...pagin, date })
      .then((resp) => {
        exportToCSV(resp.data.data, "contacts", { columns: exportColumns });
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setExporting(false);
      });
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure, you want to delete this contact?",
      icon: "warning",
      className: "w-[500px]",
      buttons: {
        cancel: {
          text: "Cancel",
          visible: true,
        },
        confirm: {
          text: "Delete",
          className: "bg-primary text-white",
        },
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        service
          .deleteContact(id)
          .then((resp) => {
            if (resp.data.success) {
              successMessage("Contact deleted successfully");
              const index = data.findIndex(
                (item: { _id: string }) => item._id === id
              );
              const shallowCopy = [...data];
              shallowCopy.splice(index, 1);
              setData(shallowCopy);
            }
          })
          .catch((error) => console.log(error));
      }
    });
  };

  const clearFilter = () => {
    setPagination({
      pageNumber: 1,
      pageSize: 100,
      source: "",
      location: "",
      contactCreatedStartDate: "",
      contactCreatedEndDate: "",
      fullName: "",
      recruiterName: "",
      isDownload: "false",
      division: "Evolution",
      recruiterbadgeId: "",
      maxPosition: "",
      groupBy: "",
      tenure: "",
      stage: "",
      email: "",
      team: "",
    });
  };

  return (
    <Fragment>
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-col mb-4">
          <h1 className="page-title">
            {tab}
          </h1>
          <p className="page-subtitle">View all {tab}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => push("/contact/states")}
            className="btn-dark"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
              />
            </svg>
            <span>Contacts By MGMT</span>
          </button>
          <button
            type="button"
            onClick={() => push("/contact/leader")}
            className="btn-dark"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
              />
            </svg>
            <span>Contacts By Leader</span>
          </button>
        </div>
        {/* <div className="flex gap-3 ">
          <select
            className={inputStyles.input}
            value={date}
            onChange={(e) => setDate( e.target.value)}
          >
            {TAGS.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
       
         
        </div> */}
      </div>

      {loadingStats ? (
        <Loader />
      ) : (
        <div className="w-full">
          <div className="grid grid-cols-9 gap-3 mb-5">
            {stats.map((item: any) => (
              <MetricCard
                key={item.stage}
                displayName={item.stage}
                value={item.count}
              />
            ))}
            <MetricCard
              displayName="Total"
              value={
                statsUniqueTotal != null
                  ? statsUniqueTotal
                  : stats.reduce(
                      (acc, item: { count: number }) => acc + item.count,
                      0
                    )
              }
            />
          </div>
        </div>
      )}

      {/* Tables */}
      {tab === "Contacts" ? (
        <Table
          data={data}
          loadingData={loading}
          pagination={pagination}
          setPagination={setPagination}
          handleExport={handleExport}
          showSearch={false}
          totalItems={totalItems}
          exporting={exporting}
          setData={setData}
          filters={
            <Filters
              clearFilter={clearFilter}
              pagination={pagination}
              setPagination={setPagination}
            />
          }
          columnFilters={
            <ColumnFilters
              pagination={pagination}
              date={date}
              setDate={setDate}
              setPagination={setPagination}
            />
          }
          columns={columns.map((item) => {
            if (NAMES.includes(item.title)) {
              return {
                ...item,
                show: showColumns.names,
              };
            }
            if (CONTACT.includes(item.title)) {
              return {
                ...item,
                show: showColumns.contacts,
              };
            }
            if (SESSIONS.includes(item.title)) {
              return {
                ...item,
                show: showColumns.sessions,
              };
            }
            if (UPGRADE.includes(item.title)) {
              return {
                ...item,
                show: showColumns.upgrade,
              };
            }
            if (LEADER.includes(item.title)) {
              return {
                ...item,
                show: showColumns.leader,
              };
            }
            if (MANAGEMENT.includes(item.title)) {
              return {
                ...item,
                show: showColumns.management,
              };
            }
            return item;
          })}
        />
      ) : tab === "Info Sessions" ? (
        <InfoSessions />
      ) : (
        <Scholarships />
      )}
    </Fragment>
  );
};

const Filters = ({ pagination, setPagination, clearFilter }) => {
  const filters = { ...pagination };
  delete filters.pageNumber;
  delete filters.pageSize;
  delete filters.isDownload;
  const hasFilters = Object.values(filters).some((item) => item);

  return (
    <div className="flex items-center flex-1 gap-4">
      <input
        className="w-full px-3 py-2 text-sm"
        type="text"
        value={pagination.fullName}
        placeholder="Search by Name"
        onChange={(e) => {
          setPagination({
            ...pagination,
            fullName: e.target.value,
          });
        }}
      />
      <input
        className="w-full px-3 py-2 text-sm"
        type="text"
        value={pagination.email}
        placeholder="Search by Email"
        onChange={(e) => {
          setPagination({
            ...pagination,
            email: e.target.value,
          });
        }}
      />
      <input
        className="w-full px-3 py-2 text-sm"
        type="text"
        value={pagination.recruiterName}
        placeholder="Search by Recruiter Name"
        onChange={(e) => {
          setPagination({
            ...pagination,
            recruiterName: e.target.value,
          });
        }}
      />
      <input
        className="w-full px-3 py-2 text-sm"
        type="text"
        placeholder="Search by Recruiter Badge ID"
        onChange={(e) => {
          setPagination({
            ...pagination,
            recruiterbadgeId: e.target.value,
          });
        }}
      />

      {hasFilters && (
        <button
          onClick={() => {
            clearFilter();
          }}
          className="btn-dark whitespace-nowrap"
        >
          Clear Filters
        </button>
      )}
    </div>
  );
};

const ColumnFilters = ({ date, setDate, pagination, setPagination }) => {
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  // const [submittedStartDate, setSubmittedStartDate] = useState<any>(null);
  // const [submittedEndDate, setSubmittedEndDate] = useState<any>(null);

  const onChangeCreated = (dates) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      setPagination({
        ...pagination,
        date: `${moment(start).format("YYYY-MM-DD")},${moment(end).format(
          "YYYY-MM-DD"
        )}`,
      });
    }
  };

  const [users, setUsers] = useState([]);
  const [tag, setTag] = useState("");
  const [loadingUser, setLoadingUser] = useState(false);

  useEffect(() => {
    setLoadingUser(true);
    service
      .getFilters(tag)
      .then((resp) => {
        setUsers(resp.data.data);
        setSelected(resp.data.data.map((item: any) => item.badgeid));
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoadingUser(false);
      });
  }, [tag]);

  // const onChangeSubmitted = (dates) => {
  //   const [start, end] = dates;

  //   if (start && end) {
  //     setPagination({
  //       ...pagination,
  //       contact_submitted_startDdate: start && formatDateY(start),
  //       contact_submitted_end_date: end && formatDateY(end),
  //     });
  //   }

  //   setSubmittedStartDate(start);
  //   setSubmittedEndDate(end);
  // };

  const filteredUsers = users.filter((item: any) =>
    item?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(filteredUsers.map((item: any) => item.badgeid));
    } else {
      setSelected([]);
    }
  };

  const handleClick = () => {
    const ids = selected.map((i) => i);
    setPagination({
      ...pagination,
      groupBy: tag,
      users: ids.length === users.length ? "All" : ids.join(","),
    });
    setShowModal(false);
  };

  return (
    <div className="flex items-center gap-4 mt-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            if (!tag) {
              setShowModal(true);
              setTag("Manager");
            } else {
              setShowModal(true);
            }
          }}
          className="btn-dark"
        >
          Heirarchy
        </button>
        {tag && (
          <button
            className="text-sm text-gray-700"
            onClick={() => {
              setTag("");
              setSelected([]);
            }}
          >
            X
          </button>
        )}
      </div>
      {/* <select
        placeholder="Stage"
        className="flex-1 px-4 py-2 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none w-72 focus:ring-0"
        onChange={(e) => {
          setTag(e.target.value);
          setPagination({
            ...pagination,
            groupBy: e.target.value,
          });
        }}
      >
        <option value="" disabled>
          Tag
        </option>
        <option value="">All</option>
        <option value="Rep">Rep</option>
        <option value="Recruiter">Recruiter</option>
        <option value="Team Lead">Team Lead</option>
        <option value="Manager">Manager</option>
        <option value="Senior Manager">Senior Manager</option>
        <option value="Regional">Regional</option>
        <option value="Senior Regional">Senior Regional</option>
        <option value="Partner">Partner</option>
      </select>

      <div className="flex-shrink-0">
        <Select
          placeholder="Users"
          className={classNames("react-select")}
          classNamePrefix="react-select"
          onChange={(e) => handleChange(e)}
          options={users.map((item: any) => ({
            label: item.name,
            value: item.badgeid,
          }))}
          isMulti
        />
      </div> */}

      {/* <div className="flex flex-1 flex-col gap-1 min-w-[150px]">
        <label className="field-label">Division</label> */}
      <select
        value={pagination.division}
        className="rounded-md flex-1 focus:border-gray-300 font-light focus:outline-none w-54 focus:ring-0 border-gray-200 px-4 py-2 text-sm text-gray-800"
        onChange={(e) => {
          setPagination({
            ...pagination,
            division: e.target.value,
          });
        }}
      >
        <option value="" disabled>
          Division
        </option>
        <option value="">All</option>
        <option value="Evolution">Evolution</option>
        <option value="Triumph">Triumph</option>
        <option value="Puma">Puma</option>
        <option value="Coalition">Coalition</option>
      </select>
      {/* </div> */}
      {/* <input
        className="px-4 py-2 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none w-60 focus:ring-0"
        type="text"
        placeholder="Location"
        value={pagination.location}
        onChange={(e) => {
          setPagination({
            ...pagination,
            location: e.target.value,
          });
        }}
      /> */}
      <select
        value={pagination.stage}
        className="flex-1 px-4 py-2 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none w-72 focus:ring-0"
        onChange={(e) => {
          setPagination({
            ...pagination,
            stage: e.target.value,
          });
        }}
      >
        <option value="" disabled>
          Stage
        </option>
        <option value="">All</option>
        <option value="Contact">Contact</option>
        <option value="Contact+">Contact+</option>
        <option value="Recruiter">Recruiter</option>
        <option value="Recruiter+">Recruiter+</option>
        <option value="Signed">Signed</option>
        <option value="Signed+">Signed+</option>
        <option value="SH Pro">SH Pro</option>
        <option value="SH Pro+">SH Pro+</option>
      </select>

      <select
        value={pagination.source}
        className="flex-1 px-4 py-2 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none w-72 focus:ring-0"
        onChange={(e) => {
          setPagination({
            ...pagination,
            source: e.target.value,
          });
        }}
      >
        <option value="" disabled>
          Source
        </option>
        <option value="">All</option>
        {/* <option value="Group Interview - Black Sparrow">
          Group Interview - Black Sparrow
        </option> */}
        <option value="Social Media Ad">Social Media Ad</option>
        <option value="Signed Rep">Signedreps</option>
        <option value="Info Session">Info Session</option>
        <option value="Direct Contact">Direct Contact</option>
        <option value="Weekly Training">Weekly Training</option>
        <option value="Networking">Networking</option>

        {/* <option value="Signed 2023">Signed 2023</option>
        <option value="Previously Signed">Previously Signed</option> */}
        <option value="Career Fair">Career Fair</option>
        <option value="ECARD INTERVIEW">ECARD INTERVIEW</option>
        <option value="Scholarship">Scholarship</option>
        <option value="Campus Recruiting">Campus Recruiting</option>
      </select>

      <select
        value={pagination.tenure}
        className="flex-1 px-4 py-2 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none w-72 focus:ring-0"
        onChange={(e) => {
          setPagination({
            ...pagination,
            tenure: e.target.value,
          });
        }}
      >
        <option value="" disabled>
          Tenure
        </option>
        <option value="">All</option>
        <option value="Rookie">Rookie</option>
        <option value="Veteran">Veteran</option>
        <option value="Sophomore">Sophomore</option>
      </select>

      <select
        value={pagination.maxPosition}
        className="flex-1 px-4 py-2 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none w-72 focus:ring-0"
        onChange={(e) => {
          setPagination({
            ...pagination,
            maxPosition: e.target.value,
          });
        }}
      >
        <option value="" disabled>
          Max Position
        </option>
        <option value="">All</option>
        <option value="Rep">Rep</option>
        <option value="Team Lead">Team Lead</option>
        <option value="Manager">Manager</option>
        <option value="Senior Manager">Senior Manager</option>
        <option value="Regional">Regional</option>
        <option value="Senior Regional">Senior Regional</option>
        <option value="Partner">Partner</option>
        <option value="VP">VP</option>
      </select>

      <select
        className="px-4 py-2 text-sm font-light text-gray-800 border-gray-200 rounded-md focus:border-gray-300 focus:outline-none w-60 focus:ring-0"
        value={date}
        onChange={(e) => {
          if (e.target.value === "CUSTOM") {
            setShowCustom(true);
          } else {
            setDate(e.target.value);
          }
        }}
      >
        <option value="" disabled>
          Contact Cretaed Date
        </option>
        {DATE.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>

      {showCustom && (
        <DatePicker
          selected={startDate}
          placeholderText="Custom Date"
          onChange={onChangeCreated}
          startDate={startDate}
          endDate={endDate}
          // showIcon
          isClearable
          className="py-2 z-100"
          customInput={
            <input
              placeholder="Select date"
              className="px-3 py-2 text-sm"
            />
          }
          selectsRange
          selectsDisabledDaysInRange
        />
      )}

      {/* <DatePicker
        selected={submittedStartDate}
        placeholderText="Contact Submitted Date"
        onChange={onChangeSubmitted}
        // showIcon
        startDate={submittedStartDate}
        endDate={submittedEndDate}
        isClearable
        className="py-2 z-100"
        customInput={
          <input
            placeholder="Select date"
            className="w-80 px-3 py-2 text-sm"
          />
        }
        selectsRange
        selectsDisabledDaysInRange
      /> */}

      {/* <input
        className="w-full px-3 py-2 text-sm"
        type="date"
        onChange={(e) =>
          setPagination({
            ...pagination,
            date_submitted: e.target.value,
          })
        }
      />

      <input
        className="w-full px-3 py-2 text-sm"
        type="date"
        onChange={(e) =>
          setPagination({
            ...pagination,
            contact_created: e.target.value,
          })
        }
      /> */}

      <Modal
        showModal={showModal}
        onClick={() => {
          setTag("");
          setSelected([]);
        }}
        setShowModal={setShowModal}
        title={"Heirarchy"}
      >
        <div className="overflow-auto h-[35rem] p-2">
          <div className="grid grid-cols-5 gap-3">
            <button
              type="button"
              onClick={() => setTag("Rep")}
              className={classNames(
                "border-black border hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Rep" }
              )}
            >
              Rep
            </button>
            <button
              type="button"
              onClick={() => setTag("Recruiter")}
              className={classNames(
                "border border-black hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Recruiter" }
              )}
            >
              Recruiter
            </button>
            <button
              type="button"
              onClick={() => setTag("Contact Leader")}
              className={classNames(
                "border border-black hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Contact Leader" }
              )}
            >
              Contact Leader
            </button>
            <button
              type="button"
              onClick={() => setTag("Team Lead")}
              className={classNames(
                "border border-black text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Team Lead" }
              )}
            >
              Team Lead
            </button>
            <button
              type="button"
              onClick={() => setTag("Manager")}
              className={classNames(
                "border border-black hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Manager" }
              )}
            >
              Manager
            </button>
            <button
              type="button"
              onClick={() => setTag("Senior Manager")}
              className={classNames(
                "border border-black hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Senior Manager" }
              )}
            >
              Senior Manager
            </button>
            <button
              type="button"
              onClick={() => setTag("Regional")}
              className={classNames(
                "border border-black hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Regional" }
              )}
            >
              Regional
            </button>
            <button
              type="button"
              onClick={() => setTag("Senior Regional")}
              className={classNames(
                "border border-black hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Senior Regional" }
              )}
            >
              Senior Regional
            </button>
            <button
              type="button"
              onClick={() => setTag("Partner")}
              className={classNames(
                "border border-black hover:bg-black hover:text-white text-center rounded-md px-3 py-2",
                { "bg-black text-white": tag === "Partner" }
              )}
            >
              Partner
            </button>
          </div>
          <div className="mt-3">
            <input
              type="text"
              onChange={(e) => setSearch(e.target.value)}
              className="w-full p-2 border rounded-md border-zinc-300"
            />
          </div>

          <label className="flex items-center justify-end gap-2 mt-2">
            <input
              type="checkbox"
              defaultChecked={
                selected.length === filteredUsers.length ||
                selected.length === 0
              }
              onChange={handleSelectAll}
            />
            Select All
          </label>

          {loadingUser ? (
            <p>Loading, please wait...</p>
          ) : (
            filteredUsers.map((item: any, index) => (
              <div key={item.badgeid} className="flex items-center gap-2 py-2 ">
                <div>
                  <input
                    checked={selected.includes(item.badgeid)}
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected((prev) => [...prev, item.badgeid]);
                      } else {
                        setSelected((prev) =>
                          prev.filter((id) => id !== item.badgeid)
                        );
                      }
                    }}
                  />
                </div>
                <p>{item.name}</p>
              </div>
            ))
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setTag("");
              setSelected([]);
              setShowModal(false);
            }}
            className="flex-1 py-2 text-white rounded-md bg-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleClick}
            className="btn-dark flex-1"
          >
            Submit
          </button>
        </div>
      </Modal>
    </div>
  );
};
